import React from 'react';
import { ApiHandler } from '@givelify/api';
import { RADIO_BUTTON_IDENTIFIER, pushEvent } from '@givelify/utils';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AppState, useAppDispatch } from 'store';
import { setDonee } from 'store/donee/actions';

export const useCoveredFees = () => {
    const { t } = useTranslation();
    const copy = React.useMemo(
        () => ({
            title: t('pages.settings.coveredFees.title'),
            notification: t('pages.settings.coveredFees.notification'),
            learnMore: t('pages.settings.coveredFees.learnMore'),
            askDonors: t('pages.settings.coveredFees.askDonors'),
            description: t('pages.settings.coveredFees.description'),
            recommended: t('pages.settings.coveredFees.recommended'),
            yes: t('labels.yes'),
            no: t('labels.no'),
        }),
        [t],
    );
    const { doneeId, enabled } = useSelector((state: AppState) => {
        return {
            doneeId: state.Donee.donee.id,
            enabled: state.Donee.donee.isDonorCoveredFeeEnabled,
        };
    });
    const dispatch = useAppDispatch();
    const [enabledLocal, setEnabled] = React.useState(enabled);
    const [showConfirmation, setShowConfirmation] = React.useState(false);
    const updateDonee = React.useCallback(
        async (enable: boolean) => {
            const updateRequest =
                await ApiHandler.instance.donees.updateCoveredFeesSettings(
                    doneeId,
                    {
                        isDonorCoveredFeeEnabled: enable,
                    },
                );
            if (updateRequest.success) {
                setEnabled(enable);
                dispatch(setDonee(updateRequest.response));
            } else {
                setEnabled(!enabledLocal);
            }
        },
        [dispatch, doneeId, enabledLocal],
    );
    const onEnable = React.useCallback(() => {
        if (enabled) return;
        pushEvent(`Click DCF Settings page <YES>_${RADIO_BUTTON_IDENTIFIER}`);
        setShowConfirmation(true);
    }, [setShowConfirmation, enabled]);
    const onDisable = React.useCallback(() => {
        if (!enabled) return;
        pushEvent(`Click DCF Settings page <NO>_${RADIO_BUTTON_IDENTIFIER}`);
        setEnabled(false);
        updateDonee(false);
    }, [enabled, updateDonee]);
    const onConfirm = React.useCallback(() => {
        setEnabled(true);
        updateDonee(true);
        setShowConfirmation(false);
    }, [setEnabled, setShowConfirmation, updateDonee]);
    const onCancel = React.useCallback(() => {
        setShowConfirmation(false);
        setEnabled(false);
    }, [setShowConfirmation, setEnabled]);
    return {
        copy,
        enabled: enabledLocal,
        onEnable,
        onDisable,
        onConfirm,
        onCancel,
        showConfirmation,
    } as const;
};
